import { useCallback, useContext, useMemo, useState, createContext, type ReactNode } from 'react';
import TUTORIALS from 'constants/tutorials';
import mainContextDefault from 'routes/mainContextDefault';
import type { TutorialBox } from 'constants/tutorialsTypes';
import type { MainContextInt, MainContextIntBase } from 'routes/types';

const MainContext = createContext(mainContextDefault as MainContextInt);

export const useMainContext = () => useContext(MainContext);

type Props = {
  children: string | ReactNode;
  value: MainContextIntBase;
}

const MainContextProvider = ({children, value}: Props) => {
  const [tutorialStep, setTutorialStep] = useState<number>(0);
  const [tutorial, setTutorial] = useState<string>('');
  const [findOutMore, setFindOutMore] = useState<string>('');
  const [howToConcept, setHowToConcept] = useState<string>('');
  const tutorialBoxes: TutorialBox[] | undefined = TUTORIALS[tutorial]?.[tutorialStep];

  const nextStep = useCallback(() => {
    if (TUTORIALS[tutorial] && tutorialStep < TUTORIALS[tutorial].length - 1) {
      setTutorialStep(tutorialStep + 1);
    } else {
      value.local.tutorials.setTutorialIsDone(tutorial);
      setTutorialStep(0);
      setTutorial('');
    }
  }, [tutorial, tutorialStep, value]);

  const startTutorial = (tutorialItem: string) => {
    setTutorial(tutorialItem);
    setTutorialStep(0);
  };

  const clearTutorials = useCallback(() => {
    value.local.tutorials.clearTutorials();
    setTutorialStep(0);
    setTutorial('');
  }, [value, setTutorial, setTutorialStep]);

  const val: MainContextInt = useMemo(() => ({
    ...value,
    tutorialBoxes,
    setTutorial: startTutorial,
    clearTutorials,
    nextStep,
    tutorialStep,
    tutorial,
    findOutMore,
    setFindOutMore,
    howToConcept,
    setHowToConcept
  }), [value, tutorialBoxes, tutorialStep, tutorial, findOutMore, howToConcept, clearTutorials, nextStep]);

  return (
    <MainContext.Provider value={val}>
      {children}
    </MainContext.Provider>
  );
};

export default MainContextProvider;
